import React, { useEffect, useState } from "react";
import { Card, Table, Spin, Button } from "antd";
import DetailsModal from "../Home/DetailsModal";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import useFeedbackService from "../../../../services/feedbackService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AllFeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const { language } = useLanguage();
  const text = dashboardContent[language].feedbackSummary;
  const { getAllFeedbacksForUserAndQuestion } = useFeedbackService();
  const handleExportPDF = () => {
    const doc = new jsPDF();
    const lineHeight = 7;
    let y = 22;

    // Titel der Übersicht
    doc.setFontSize(18);
    doc.text(text.exportTitle || "Feedback Übersicht", 14, y);

    // Übersichtstabelle mit X / 5 im Rating
    autoTable(doc, {
      startY: y + 8,
      head: [
        [text.table.columnForm, text.table.columnFrom, text.table.columnRating],
      ],
      body: feedbacks.map((fb) => {
        const raw = parseFloat(fb.rating) || 0;
        const ratingPdf = isNaN(raw) ? "-" : `${raw} / 5`;
        return [fb.category, fb.fromText, ratingPdf];
      }),
      styles: { fontSize: 10 },
    });

    // Spacer & Details-Überschrift
    y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text(text.detailsTitle || "Feedback Details", 14, y);
    y += 6;

    doc.setFontSize(11);

    // Für jedes Feedback eine eigene Sektion
    feedbacks.forEach((fb, index) => {
      // QA-Tabelle mit "X / 5" und Nicht‑Ausgewählt bei MC
      const qaRows = fb.responses.map((res) => {
        const question =
          res.questionText || res.questionId?.questionText || "Frage unbekannt";

        const selected = Array.isArray(res.answer)
          ? res.answer.map(String)
          : res.answer != null
          ? [String(res.answer)]
          : [];

        let answerText = selected.join(", ");
        // 1) Spezieller yesno‑Fall: splitte "Ja, Kommentar"
        if (res.questionId?.type === "yesno") {
          const raw = res.answer || "";
          const [choice, comment] = raw.split(/,\s*(.+)/);
          answerText = choice || "Keine Antwort";
          if (comment) {
            answerText += `\n${comment}`;
          }
        } else {
          // 2) Alle anderen Typen bleiben wie gehabt:
          answerText = selected.join(", ");
        }

        // Erzwinge "X / 5" bei Stern-Rating oder Gesamtbewertung
        const isStarRating =
          res.questionId?.type === "star-rating" ||
          question.toLowerCase().includes("gesamtbewertung");
        if (isStarRating && answerText) {
          const n = parseFloat(answerText);
          if (!isNaN(n)) answerText = `${n} / 5`;
        }

        // Fallback bei Leer-Antwort
        if (!answerText) {
          answerText = "Keine Antwort";
        }

        // Bei Multiple-Choice: Nicht ausgewählte Optionen anhängen
        if (
          res.questionId?.type === "multiple-choice" &&
          Array.isArray(res.questionId.options)
        ) {
          const notSelected = res.questionId.options.filter(
            (opt) => !selected.includes(opt)
          );
          if (notSelected.length) {
            answerText += `\n\nNicht ausgewählt:\n${notSelected.join(", ")}`;
          }
        }

        return [question, answerText];
      });
      const headerLines = 3; // z.B. Titel, Formular, Datum
      const headerHeight = headerLines * lineHeight; // lineHeight aus deinem Code
      const rowHeight = lineHeight + 2; // geschätzte Zeilenhöhe inkl. Padding
      const tableRows = qaRows.length + 1; // +1 für den Tabellenkopf
      const tableHeight = tableRows * rowHeight;
      const blockHeight = headerHeight + tableHeight + 10; // + etwas Puffer

      const pageHeight = doc.internal.pageSize.height;
      const bottomMargin = 10;

      // 4) Seitenumbruch, wenn nicht genug Platz für Header+Tabelle
      if (y + blockHeight > pageHeight - bottomMargin) {
        doc.addPage();
        y = 20; // neuer Start-Y auf frischer Seite
      }

      // Header mit X / 5
      const raw = parseFloat(fb.rating) || 0;
      const ratingPdf = isNaN(raw) ? "-" : `${raw} / 5`;
      doc.setFontSize(13);
      doc.setFont(undefined, "bold");
      doc.text(
        `Feedback ${index + 1} – ${ratingPdf} – von ${fb.fromText}`,
        14,
        y
      );
      y += lineHeight;

      // Formularname & Datum
      doc.setFont(undefined, "normal");
      doc.text(`Formular: ${fb.category}`, 14, y);
      y += lineHeight;
      const date = fb.date
        ? new Date(fb.date).toLocaleString(
            language === "de" ? "de-DE" : "en-US"
          )
        : "-";
      doc.text(`Datum: ${date}`, 14, y);
      y += lineHeight;

      autoTable(doc, {
        startY: y + 2,
        head: [["Frage", "Antwort"]],
        body: qaRows,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [255, 165, 0] },
        theme: "striped",
        margin: { left: 14, right: 14 },
        pageBreak: "avoid",
        rowPageBreak: "avoid",
      });

      y = doc.lastAutoTable.finalY + 10;
    });

    // Fußzeile mit "Seite X von Y"
    const pageCount = doc.getNumberOfPages();
    const { width: W, height: H } = doc.internal.pageSize;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Seite ${i} von ${pageCount}`, W / 2, H - 10, {
        align: "center",
      });
    }

    // PDF speichern
    doc.save("feedbacks.pdf");
  };

  useEffect(() => {
    const fetchFeedbacks = async () => {
      setLoading(true);
      const fetchedFeedbacks = await getAllFeedbacksForUserAndQuestion();
      if (fetchedFeedbacks) {
        const formattedFeedbacks = fetchedFeedbacks.map((feedback, index) => {
          const first = feedback.sender?.firstname?.trim() || "";
          const last = feedback.sender?.lastname?.trim() || "";
          const mail = feedback.sender?.email?.trim() || "";
          const fullName = `${first} ${last}`.trim();
          const fullText = `${fullName} - ${mail}`.trim();

          const isAnon = first.toLowerCase() === "anonym";
          const fromNode = isAnon ? (
            "UNBEKANNT"
          ) : (
            <>
              {first} {last}
              <br />
              {mail}
            </>
          );

          const ratingValue = extractNumericRating(feedback);
          console.log(ratingValue);

          return {
            key: feedback.id || index,
            category: feedback.form?.name || "Unbekanntes Formular",
            fromText: isAnon ? "UNBEKANNT" : fullText,
            from: fromNode,
            email: mail,
            rating: formatRating(feedback),
            date: feedback.submittedAt ? new Date(feedback.submittedAt) : null,
            responses: feedback.responses || [],
            form: feedback.form,
            numericRating: ratingValue || 0,
            images: feedback.images || [],
          };
        });

        setFeedbacks(formattedFeedbacks);
      }
      setLoading(false);
    };

    fetchFeedbacks();
  }, []);

  const extractOverall = (responses) =>
    responses.find((res) => res.questionText === "Gesamtbewertung");

  const extractNumericRating = (fb) => {
    const overall = extractOverall(fb.responses);
    if (overall && overall.answer !== undefined) {
      const n = parseFloat(overall.answer);
      return isNaN(n) ? null : n;
    }
    return null;
  };

  const formatRating = (feedback) => {
    // Option 1: Suche nach questionText "Gesamtbewertung" (wie extractNumericRating)
    const ratingResponse = feedback.responses.find(
      (res) => res.questionText === "Gesamtbewertung"
    );

    // Option 2: Oder suche nach questionId._id falls du bei der ID bleiben willst
    // const ratingResponse = feedback.responses.find(
    //   (res) => res.questionId?._id === "679f639effa430a7f0756e34"
    // );

    if (ratingResponse && ratingResponse.answer !== undefined) {
      return `${ratingResponse.answer} / 5`;
    }
    return "-";
  };

  const handleShow = (record) => {
    setSelectedFeedback(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: text.table.columnDate || "Datum",
      dataIndex: "date",
      key: "date",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString(
              language === "de" ? "de-DE" : "en-US"
            )
          : "-",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: text.table.columnForm || "Formular",
      dataIndex: "category",
      key: "category",
    },
    {
      title: text.table.columnEmail || "E-Mail",
      dataIndex: "email",
      key: "email",
      responsive: ["md"],
      render: (email) => email || "-",
    },
    {
      title: text.table.columnFrom || "Von",
      dataIndex: "from",
      key: "from",
    },
    {
      title: text.table.columnRating || "Bewertung",
      dataIndex: "rating",
      key: "rating",
      sorter: (a, b) => a.numericRating - b.numericRating,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: text.table.columnAction || "Aktion",
      key: "action",
      render: (_, record) => (
        <Button
          className="bg-orange-500 border-orange-500 hover:bg-orange-600 text-white"
          onClick={() => handleShow(record)}
        >
          {text.table.actionShow}
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      <Card
        title={text.title}
        className="shadow-lg border-orange-500"
        headStyle={{ backgroundColor: "#f97316", color: "#fff" }}
        extra={
          <Button
            className="bg-green-600 border-green-600 hover:bg-green-700 text-white"
            onClick={handleExportPDF}
          >
            {text.exportButton || "PDF Exportieren"}
          </Button>
        }
      >
        {loading ? (
          <div className="text-center py-8">
            <Spin size="large" />
          </div>
        ) : (
          <div className="overflow-x-scroll custom-scrollbar">
            <Table
              columns={columns}
              dataSource={feedbacks}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: text.table.emptyText }}
            />
          </div>
        )}
      </Card>
      <DetailsModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        feedback={selectedFeedback}
      />
    </div>
  );
};

export default AllFeedbackPage;
